<template>
  <div v-if="totalItems > 0" class="ui-paging">
    <div class="pagination-info" v-if="showInfo">
      {{ t('common.paging.info', { total: totalItems, start: startItem, end: endItem }) }}
    </div>
    <div class="pagination-controls">
      <UiButton variant="secondary-line" size="small" :disabled="currentPage === 1" @click="goToPrevious">
        {{ t('common.paging.previous') }}
      </UiButton>
      <div class="pagination-numbers">
        <button v-for="page in visiblePages" :key="page" class="page-number" :class="{ active: page === currentPage }" @click="goToPage(page)">
          {{ page }}
        </button>
      </div>
      <UiButton variant="secondary-line" size="small" :disabled="currentPage === totalPages" @click="goToNext">
        {{ t('common.paging.next') }}
      </UiButton>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import UiButton from './UiButton.vue';

const { t } = useI18n();

const props = defineProps({
  // 현재 페이지
  currentPage: {
    type: Number,
    required: true,
    default: 1,
  },
  // 전체 아이템 수
  totalItems: {
    type: Number,
    required: true,
    default: 0,
  },
  // 페이지당 아이템 수
  itemsPerPage: {
    type: Number,
    default: 10,
  },
  // 표시할 최대 페이지 번호 수
  maxVisiblePages: {
    type: Number,
    default: 10,
  },
  // 정보 표시 여부
  showInfo: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:currentPage', 'page-change']);

// 전체 페이지 수
const totalPages = computed(() => {
  return Math.ceil(props.totalItems / props.itemsPerPage);
});

// 시작 아이템 번호
const startItem = computed(() => {
  return (props.currentPage - 1) * props.itemsPerPage + 1;
});

// 끝 아이템 번호
const endItem = computed(() => {
  return Math.min(props.currentPage * props.itemsPerPage, props.totalItems);
});

// 표시할 페이지 번호들
const visiblePages = computed(() => {
  const pages = [];
  const maxVisible = props.maxVisiblePages;
  let start = Math.max(1, props.currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages.value, start + maxVisible - 1);

  // 끝에서 시작점 조정
  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1);
  }

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  return pages;
});

// 페이지 이동
const goToPage = page => {
  if (page >= 1 && page <= totalPages.value && page !== props.currentPage) {
    emit('update:currentPage', page);
    emit('page-change', page);
  }
};

// 이전 페이지
const goToPrevious = () => {
  if (props.currentPage > 1) {
    goToPage(props.currentPage - 1);
  }
};

// 다음 페이지
const goToNext = () => {
  if (props.currentPage < totalPages.value) {
    goToPage(props.currentPage + 1);
  }
};
</script>

<style scoped lang="scss">
.ui-paging {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 0.0625rem solid #e8eaed;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  .pagination-info {
    text-align: center;
    font-size: 0.8125rem;
    color: var(--color-gray-30, #b1b8be);
  }

  .pagination-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;

    .pagination-numbers {
      display: flex;
      gap: 0.25rem;

      .page-number {
        min-width: 2rem;
        height: 2rem;
        padding: 0 0.5rem;
        border: 0.0625rem solid #e8eaed;
        background-color: #fff;
        color: #202124;
        font-size: 0.875rem;
        border-radius: 0.25rem;
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover:not(.active) {
          background-color: #f8fafc;
          border-color: var(--color-primary-50, #0af);
        }

        &.active {
          background-color: var(--color-primary-50, #0af);
          color: #fff;
          border-color: var(--color-primary-50, #0af);
          font-weight: 600;
        }
      }
    }
  }
}
</style>
